// src/components/exam/charts/ProgressMetricsCards.jsx

import React from 'react';
import { TrendingUp, TrendingDown, Minus, Award, Target, Clock, BarChart3 } from 'lucide-react';
import { getGoetheModuleSummary } from '../../../utils/goetheExamDisplay';

const ProgressMetricsCards = ({ overallMetrics, timeAnalysis, currentExam, comparison }) => {
  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (value < 0) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const goetheCurrent = currentExam ? getGoetheModuleSummary(currentExam) : null;
  const goetheMetrics = overallMetrics?.goethe;
  const isGoethe = !!goetheCurrent;

  const metrics = [];

  // 1. Current Score (Goethe: X/3 modules passed)
  if (currentExam) {
    if (isGoethe) {
      metrics.push({
        icon: <Target className="w-5 h-5 text-blue-600" />,
        label: 'Current Score',
        value: `${goetheCurrent.displayShort}`,
        subtitle: `modules passed (of ${goetheCurrent.total})`,
        color: 'blue'
      });
    } else {
      metrics.push({
        icon: <Target className="w-5 h-5 text-blue-600" />,
        label: 'Current Score',
        value: `${Math.round(currentExam.overallScore || 0)}/${currentExam.maxScore || 225}`,
        subtitle: `Out of ${currentExam.maxScore || 225}`,
        color: 'blue'
      });
    }
  }

  // 2. Average Score (Goethe: X/3 avg)
  if (overallMetrics) {
    if (goetheMetrics) {
      metrics.push({
        icon: <BarChart3 className="w-5 h-5 text-purple-600" />,
        label: 'Average Score',
        value: `${goetheMetrics.averagePassed}/${goetheMetrics.totalModules}`,
        subtitle: `Over ${overallMetrics.totalExams || 0} exams`,
        color: 'purple'
      });
    } else {
      const avgScore = overallMetrics.averageScore || 0;
      const maxScore = currentExam?.maxScore || 225;
      metrics.push({
        icon: <BarChart3 className="w-5 h-5 text-purple-600" />,
        label: 'Average Score',
        value: `${Math.round(avgScore)}/${maxScore}`,
        subtitle: `Over ${overallMetrics.totalExams || 0} exams`,
        color: 'purple'
      });
    }
  }

  // 3. Best Score (Goethe: X/3 best)
  if (overallMetrics && (overallMetrics.bestScore > 0 || goetheMetrics)) {
    if (goetheMetrics) {
      metrics.push({
        icon: <Award className="w-5 h-5 text-yellow-600" />,
        label: 'Best Score',
        value: `${goetheMetrics.bestPassed}/${goetheMetrics.totalModules}`,
        subtitle: 'Personal record',
        color: 'yellow'
      });
    } else {
      const maxScore = currentExam?.maxScore || 225;
      metrics.push({
        icon: <Award className="w-5 h-5 text-yellow-600" />,
        label: 'Best Score',
        value: `${overallMetrics.bestScore}/${maxScore}`,
        subtitle: 'Personal record',
        color: 'yellow'
      });
    }
  }

  // 4. Overall Progress - keep this as is (trend)
  if (overallMetrics) {
    metrics.push({
      icon: getTrendIcon(overallMetrics.improvementRate),
      label: 'Overall Progress',
      value: `${overallMetrics.improvementRate > 0 ? '+' : ''}${overallMetrics.improvementRate.toFixed(1)}%`,
      subtitle: overallMetrics.improvementRate > 0 ? 'Improving' : overallMetrics.improvementRate < 0 ? 'Declining' : 'Stable',
      color: overallMetrics.improvementRate > 0 ? 'green' : overallMetrics.improvementRate < 0 ? 'red' : 'gray'
    });
  }

  // 5. Avg. Time
  if (timeAnalysis) {
    metrics.push({
      icon: <Clock className="w-5 h-5 text-blue-600" />,
      label: 'Avg. Time',
      value: `${timeAnalysis.averageTime || 0}m`,
      subtitle: `Last: ${timeAnalysis.lastTime || 0}m`,
      color: 'blue'
    });
  }

  // 6. vs. Previous - comparison
  if (comparison && comparison.hasComparison) {
    metrics.push({
      icon: getTrendIcon(comparison.scoreDifference),
      label: 'vs. Previous',
      value: `${comparison.scoreDifference > 0 ? '+' : ''}${comparison.scoreDifference}`,
      subtitle: comparison.scoreDifference > 0 ? 'Better' : comparison.scoreDifference < 0 ? 'Worse' : 'Same',
      color: comparison.scoreDifference > 0 ? 'green' : comparison.scoreDifference < 0 ? 'red' : 'gray'
    });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {metrics.map((metric, index) => (
        <div
          key={index}
          className={`bg-${metric.color}-50 border border-${metric.color}-200 rounded-lg p-4`}
        >
          <div className="flex items-start justify-between mb-3">
            <div>{metric.icon}</div>
          </div>
          <h3 className="text-sm font-medium text-gray-600 mb-1">{metric.label}</h3>
          <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
          <p className="text-xs text-gray-500 mt-1">{metric.subtitle}</p>
        </div>
      ))}
    </div>
  );
};

export default ProgressMetricsCards;
